// Canonical seconds→units billing math, shared by every surface that turns
// elapsed time into billable units. Kept in one place so the web app's display
// rule matches the backend's `calculateUnits` byte-for-byte — export bills
// straight from `time_entry_groups.duration_s` and is not coupled to this code,
// so any drift here means attorneys see one number and bill another.

/** Smallest billable increment (6 minutes). The grid floor is always ≥ this. */
export const MIN_INCREMENT = 0.1;

/**
 * Resolve the firm's billing increment from the `minimum_units` setting.
 * `false` ⇒ 0.1h grid, `true` ⇒ 0.2h grid. (The frontend has no per-matter
 * `increment` yet, so this global setting is the only knob available.)
 */
export function incrementForMinimumUnits(minimumUnits: boolean): number {
	return minimumUnits ? 0.2 : MIN_INCREMENT;
}

/**
 * Convert RAW (non-grid-aligned) seconds to billable units:
 *   units = ceil(seconds / (increment * 3600)) * increment
 *
 * Use this ONLY for raw sums the backend has not yet rounded — the merge/group
 * preview candidates and the `durationSeconds`-null child-sum fallback.
 *
 * Do NOT use it on a backend `durationSeconds`: that value is already aligned to
 * the matter's grid, so `durationSeconds / 3600` is exact. Re-applying this ceil
 * with a 0.1/0.2 increment corrupts coarser matters (a 0.25h matter's 900s would
 * ceil to 0.3 instead of 0.25).
 */
export function unitsFromSeconds(
	seconds: number,
	increment: number = MIN_INCREMENT,
): number {
	if (seconds <= 0) return 0;
	const inc = Math.max(increment, MIN_INCREMENT);
	return parseFloat((Math.ceil(seconds / (inc * 3600)) * inc).toFixed(2));
}
